import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Settings,
  Plus,
  Search,
  Play,
  Pause,
  Edit2,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Filter,
  Loader2,
  FileText,
  Package,
  DollarSign,
  Truck,
  Pill,
  Eye,
  TestTube } from
'lucide-react';
import {
  getRules,
  getRuleById,
  createRule,
  updateRule,
  testRule } from
'../services/calendarAPI';

const MODULE_ICONS = {
  WMS: { icon: Package, color: 'bg-blue-500' },
  Finance: { icon: DollarSign, color: 'bg-purple-500' },
  EzParcel: { icon: Truck, color: 'bg-orange-500' },
  PharmaScan: { icon: Pill, color: 'bg-green-500' }
};

const STATUS_BADGES = {
  draft: { variant: 'secondary', label: 'Draft' },
  active: { variant: 'default', label: 'Active', className: 'bg-green-500' },
  paused: { variant: 'warning', label: 'Paused' },
  archived: { variant: 'outline', label: 'Archived' }
};

export default function EventRulesPage() {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [testResult, setTestResult] = useState(null);
  const [testData, setTestData] = useState('{\n  "entity_type": "asn",\n  "entity_id": "123",\n  "asn": {\n    "id": "123",\n    "reference": "ASN-2025-001",\n    "supplier_name": "Supplier Co.",\n    "po_reference": "PO-2025-100",\n    "status": "pending",\n    "eta": "2025-01-30T10:00:00Z"\n  }\n}');

  // New rule form state
  const [newRule, setNewRule] = useState({
    name: '',
    code: '',
    description: '',
    trigger_event_type: '',
    source_module: 'WMS',
    event_template: {
      title_template: '',
      description_template: '',
      category: 'other',
      priority: 'normal',
      all_day: false
    },
    audience_config: {
      calendars: [{ type: 'team', department: 'warehouse' }],
      roles: []
    },
    conditions: [],
    reminder_config: [],
    idempotency_key_template: ''
  });

  // Load rules
  useEffect(() => {
    const loadRules = async () => {
      setLoading(true);
      try {
        const params = {};
        if (moduleFilter !== 'all') params.source_module = moduleFilter;
        if (statusFilter !== 'all') params.status = statusFilter;

        const data = await getRules(params);
        setRules(data);
      } catch (error) {
        console.error('Failed to load rules:', error);
        toast.error('Failed to load event rules');
      } finally {
        setLoading(false);
      }
    };
    loadRules();
  }, [moduleFilter, statusFilter]);

  // Filter rules by search
  const filteredRules = rules.filter((rule) =>
  rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
  rule.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // View rule details
  const handleViewRule = async (rule) => {
    try {
      const fullRule = await getRuleById(rule.id);
      setSelectedRule(fullRule);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Failed to load rule details:', error);
      toast.error('Failed to load rule details');
    }
  };

  // Toggle rule status
  const handleToggleStatus = async (rule) => {
    try {
      const newStatus = rule.status === 'active' ? 'paused' : 'active';
      await updateRule(rule.id, { status: newStatus });
      toast.success(`Rule ${newStatus === 'active' ? 'activated' : 'paused'}`);

      // Refresh rules
      const params = {};
      if (moduleFilter !== 'all') params.source_module = moduleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await getRules(params);
      setRules(data);
    } catch (error) {
      console.error('Failed to update rule:', error);
      toast.error('Failed to update rule status');
    }
  };

  // Create new rule
  const handleCreateRule = async () => {
    try {
      await createRule(newRule);
      toast.success('Rule created successfully');
      setShowCreateDialog(false);
      setNewRule({
        name: '',
        code: '',
        description: '',
        trigger_event_type: '',
        source_module: 'WMS',
        event_template: {
          title_template: '',
          description_template: '',
          category: 'other',
          priority: 'normal',
          all_day: false
        },
        audience_config: {
          calendars: [{ type: 'team', department: 'warehouse' }],
          roles: []
        },
        conditions: [],
        reminder_config: [],
        idempotency_key_template: ''
      });

      // Refresh rules
      const data = await getRules();
      setRules(data);
    } catch (error) {
      console.error('Failed to create rule:', error);
      toast.error('Failed to create rule');
    }
  };

  // Test rule
  const handleTestRule = async () => {
    if (!selectedRule) return;

    try {
      const sampleData = JSON.parse(testData);
      const result = await testRule(selectedRule.id, sampleData);
      setTestResult(result);
    } catch (error) {
      console.error('Failed to test rule:', error);
      toast.error('Failed to test rule. Check JSON format.');
    }
  };

  // Open test dialog
  const openTestDialog = (rule) => {
    setSelectedRule(rule);
    setTestResult(null);
    setShowTestDialog(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="event-rules-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Settings className="w-7 h-7 text-purple-600" />{t("eventRulesEngine", "Event Rules Engine")}

          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t("configureAutomaticEvent", "Configure automatic event generation from domain events")}

          </p>
        </div>
        
        <Button
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-rule-btn">
          
          <Plus className="w-4 h-4 mr-1" />{t("createRule", "Create Rule")}

        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("totalRules", "Total Rules")}</p>
                <p className="text-2xl font-bold">{rules.length}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("active", "Active")}</p>
                <p className="text-2xl font-bold text-green-600">
                  {rules.filter((r) => r.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("paused", "Paused")}</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {rules.filter((r) => r.status === 'paused').length}
                </p>
              </div>
              <Pause className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("totalExecutions", "Total Executions")}</p>
                <p className="text-2xl font-bold">
                  {rules.reduce((sum, r) => sum + (r.execution_count || 0), 0)}
                </p>
              </div>
              <Zap className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search rules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-rules" />
              
            </div>
            
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-48" data-testid="module-filter">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allModules", "All Modules")}</SelectItem>
                <SelectItem value="WMS">{t("wms", "WMS")}</SelectItem>
                <SelectItem value="Finance">{t("finance", "Finance")}</SelectItem>
                <SelectItem value="EzParcel">{t("ezparcel", "EzParcel")}</SelectItem>
                <SelectItem value="PharmaScan">{t("pharmascan", "PharmaScan")}</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="active">{t("active", "Active")}</SelectItem>
                <SelectItem value="paused">{t("paused", "Paused")}</SelectItem>
                <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                <SelectItem value="archived">{t("archived", "Archived")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("eventRules", "Event Rules")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ?
          <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div> :
          filteredRules.length === 0 ?
          <div className="text-center py-12 text-slate-500">
              <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t("noRulesFound", "No rules found")}</p>
            </div> :

          <div className="space-y-4">
              {filteredRules.map((rule) => {
              const module = MODULE_ICONS[rule.source_module] || { icon: FileText, color: 'bg-slate-500' };
              const Icon = module.icon;
              const status = STATUS_BADGES[rule.status] || STATUS_BADGES.draft;

              return (
                <div
                  key={rule.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  data-testid={`rule-item-${rule.id}`}>
                  
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg ${module.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-900 dark:text-white">{rule.name}</h4>
                          <Badge variant={status.variant} className={status.className}>
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">
                          <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded text-xs">{rule.code}</code>
                          <span className="mx-2">•</span>{t("trigger", "Trigger:")}
                        {rule.trigger_event_type}
                        </p>
                        {rule.description &&
                      <p className="text-sm text-slate-400 mt-1 truncate max-w-md">{rule.description}</p>
                      }
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right text-sm">
                        <p className="text-slate-500">{t("executions", "Executions")}</p>
                        <p className="font-semibold">{rule.execution_count || 0}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewRule(rule)}
                        title="View Details"
                        data-testid={`view-rule-${rule.id}`}>
                        
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openTestDialog(rule)}
                        title="Test Rule"
                        data-testid={`test-rule-${rule.id}`}>
                        
                          <TestTube className="w-4 h-4" />
                        </Button>
                        
                        <Switch
                        checked={rule.status === 'active'}
                        onCheckedChange={() => handleToggleStatus(rule)}
                        data-testid={`toggle-rule-${rule.id}`} />
                      
                      </div>
                    </div>
                  </div>);

            })}
            </div>
          }
        </CardContent>
      </Card>

      {/* Rule Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedRule &&
          <>
              <DialogHeader>
                <DialogTitle>{selectedRule.name}</DialogTitle>
                <DialogDescription>
                  <code className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{selectedRule.code}</code>
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">{t("sourceModule", "Source Module")}</Label>
                    <p className="font-medium">{selectedRule.source_module}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">{t("triggerEvent", "Trigger Event")}</Label>
                    <p className="font-medium">{selectedRule.trigger_event_type}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">{t("status", "Status")}</Label>
                    <Badge variant={STATUS_BADGES[selectedRule.status]?.variant} className="mt-1">
                      {STATUS_BADGES[selectedRule.status]?.label}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">{t("executions", "Executions")}</Label>
                    <p className="font-medium">{selectedRule.execution_count || 0}</p>
                  </div>
                </div>
                
                {selectedRule.description &&
              <div>
                    <Label className="text-slate-500">{t("description", "Description")}</Label>
                    <p>{selectedRule.description}</p>
                  </div>
              }
                
                {/* Conditions */}
                {selectedRule.conditions && selectedRule.conditions.length > 0 &&
              <div>
                    <Label className="text-slate-500">{t("conditions", "Conditions")}</Label>
                    <div className="mt-2 space-y-2">
                      {selectedRule.conditions.map((cond, i) =>
                  <div key={i} className="bg-slate-100 dark:bg-slate-800 p-2 rounded text-sm">
                          <code>{cond.field} {cond.operator} {JSON.stringify(cond.value)}</code>
                        </div>
                  )}
                    </div>
                  </div>
              }
                
                {/* Event Template */}
                <div>
                  <Label className="text-slate-500">{t("eventTemplate", "Event Template")}</Label>
                  <div className="mt-2 bg-slate-100 dark:bg-slate-800 p-3 rounded">
                    <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(selectedRule.event_template, null, 2)}
                    </pre>
                  </div>
                </div>
                
                {/* Audience Config */}
                <div>
                  <Label className="text-slate-500">{t("audienceConfiguration", "Audience Configuration")}</Label>
                  <div className="mt-2 bg-slate-100 dark:bg-slate-800 p-3 rounded">
                    <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(selectedRule.audience_config, null, 2)}
                    </pre>
                  </div>
                </div>
                
                {/* Reminder Config */}
                {selectedRule.reminder_config && selectedRule.reminder_config.length > 0 &&
              <div>
                    <Label className="text-slate-500">{t("reminders", "Reminders")}</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedRule.reminder_config.map((rem, i) =>
                  <Badge key={i} variant="outline">
                          {rem.offset} - {rem.channels?.join(', ')}
                        </Badge>
                  )}
                    </div>
                  </div>
              }
                
                {/* Idempotency Key */}
                {selectedRule.idempotency_key_template &&
              <div>
                    <Label className="text-slate-500">{t("idempotencyKeyTemplate", "Idempotency Key Template")}</Label>
                    <code className="block mt-1 bg-slate-100 dark:bg-slate-800 p-2 rounded text-sm">
                      {selectedRule.idempotency_key_template}
                    </code>
                  </div>
              }
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>{t("close", "Close")}

              </Button>
              </DialogFooter>
            </>
          }
        </DialogContent>
      </Dialog>

      {/* Create Rule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("createEventRule", "Create Event Rule")}</DialogTitle>
            <DialogDescription>{t("defineARule", "Define a rule to automatically create calendar events from domain events")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("ruleName", "Rule Name *")}</Label>
                <Input
                  value={newRule.name}
                  onChange={(e) => setNewRule((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., ASN Expected Arrival"
                  className="mt-1"
                  data-testid="new-rule-name" />
                
              </div>
              <div>
                <Label>{t("ruleCode", "Rule Code *")}</Label>
                <Input
                  value={newRule.code}
                  onChange={(e) => setNewRule((prev) => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                  placeholder="e.g., WMS_ASN_ARRIVAL"
                  className="mt-1"
                  data-testid="new-rule-code" />
                
              </div>
            </div>
            
            <div>
              <Label>{t("description", "Description")}</Label>
              <Textarea
                value={newRule.description}
                onChange={(e) => setNewRule((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this rule does..."
                className="mt-1"
                rows={2} />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("sourceModule", "Source Module *")}</Label>
                <Select
                  value={newRule.source_module}
                  onValueChange={(val) => setNewRule((prev) => ({ ...prev, source_module: val }))}>
                  
                  <SelectTrigger className="mt-1" data-testid="new-rule-module">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WMS">{t("wms", "WMS")}</SelectItem>
                    <SelectItem value="Finance">{t("finance", "Finance")}</SelectItem>
                    <SelectItem value="EzParcel">{t("ezparcel", "EzParcel")}</SelectItem>
                    <SelectItem value="PharmaScan">{t("pharmascan", "PharmaScan")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("triggerEventType", "Trigger Event Type *")}</Label>
                <Input
                  value={newRule.trigger_event_type}
                  onChange={(e) => setNewRule((prev) => ({ ...prev, trigger_event_type: e.target.value }))}
                  placeholder="e.g., asn.created"
                  className="mt-1"
                  data-testid="new-rule-trigger" />
                
              </div>
            </div>
            
            <div>
              <Label>{t("eventTitleTemplate", "Event Title Template *")}</Label>
              <Input
                value={newRule.event_template.title_template}
                onChange={(e) => setNewRule((prev) => ({
                  ...prev,
                  event_template: { ...prev.event_template, title_template: e.target.value }
                }))}
                placeholder='e.g., ASN {{asn.reference}} - Expected Arrival'
                className="mt-1"
                data-testid="new-rule-title-template" />
              
              <p className="text-xs text-slate-500 mt-1">{t("use", "Use")}{'{{field}}'}{t("forDynamicValues", "for dynamic values")}</p>
            </div>
            
            <div>
              <Label>{t("eventDescriptionTemplate", "Event Description Template")}</Label>
              <Textarea
                value={newRule.event_template.description_template}
                onChange={(e) => setNewRule((prev) => ({
                  ...prev,
                  event_template: { ...prev.event_template, description_template: e.target.value }
                }))}
                placeholder='e.g., Supplier: {{asn.supplier_name}}'
                className="mt-1"
                rows={2} />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("eventCategory", "Event Category")}</Label>
                <Select
                  value={newRule.event_template.category}
                  onValueChange={(val) => setNewRule((prev) => ({
                    ...prev,
                    event_template: { ...prev.event_template, category: val }
                  }))}>
                  
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receiving">{t("receiving", "Receiving")}</SelectItem>
                    <SelectItem value="shipping">{t("shipping", "Shipping")}</SelectItem>
                    <SelectItem value="finance">{t("finance", "Finance")}</SelectItem>
                    <SelectItem value="compliance">{t("compliance", "Compliance")}</SelectItem>
                    <SelectItem value="delivery">{t("delivery", "Delivery")}</SelectItem>
                    <SelectItem value="other">{t("other", "Other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("eventPriority", "Event Priority")}</Label>
                <Select
                  value={newRule.event_template.priority}
                  onValueChange={(val) => setNewRule((prev) => ({
                    ...prev,
                    event_template: { ...prev.event_template, priority: val }
                  }))}>
                  
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("low", "Low")}</SelectItem>
                    <SelectItem value="normal">{t("normal", "Normal")}</SelectItem>
                    <SelectItem value="high">{t("high", "High")}</SelectItem>
                    <SelectItem value="critical">{t("critical", "Critical")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>{t("idempotencyKeyTemplate", "Idempotency Key Template")}</Label>
              <Input
                value={newRule.idempotency_key_template}
                onChange={(e) => setNewRule((prev) => ({ ...prev, idempotency_key_template: e.target.value }))}
                placeholder='e.g., asn_arrival_{{asn.id}}'
                className="mt-1" />
              
              <p className="text-xs text-slate-500 mt-1">{t("preventsDuplicateEvents", "Prevents duplicate events for the same source entity")}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleCreateRule}
              disabled={!newRule.name || !newRule.code || !newRule.trigger_event_type || !newRule.event_template.title_template}
              data-testid="create-rule-submit">{t("createRule", "Create Rule")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Rule Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("testRule", "Test Rule:")}{selectedRule?.name}</DialogTitle>
            <DialogDescription>{t("enterSampleEvent", "Enter sample event data to test if the rule would create a calendar event")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("sampleEventData", "Sample Event Data (JSON)")}</Label>
              <Textarea
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                className="mt-1 font-mono text-sm"
                rows={12}
                data-testid="test-rule-data" />
              
            </div>
            
            {testResult &&
            <div className={`p-4 rounded-lg ${testResult.would_create_event ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.would_create_event ?
                <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">{t("eventWouldBe", "Event would be created")}</span>
                    </> :

                <>
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-yellow-800">{t("conditionsNotMet", "Conditions not met - no event")}</span>
                    </>
                }
                </div>
                
                {testResult.preview &&
              <div className="mt-3 bg-white p-3 rounded border">
                    <p className="text-sm text-slate-500">{t("preview", "Preview:")}</p>
                    <p className="font-medium mt-1">{testResult.preview.title}</p>
                    {testResult.preview.description &&
                <p className="text-sm text-slate-600 mt-1">{testResult.preview.description}</p>
                }
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{testResult.preview.category}</Badge>
                      <Badge variant="outline">{testResult.preview.priority}</Badge>
                    </div>
                  </div>
              }
              </div>
            }
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>{t("close", "Close")}

            </Button>
            <Button onClick={handleTestRule} data-testid="run-test-btn">
              <TestTube className="w-4 h-4 mr-1" />{t("runTest", "Run Test")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}